import classNames from "classnames";
import { AlertCircleIcon, CheckIcon, SpinnerIcon } from "@icons";
import type { ToolTraceEntry } from "@common/schemas/domains/agentChat/agent-chat.schema";
import type { LiveToolRow } from "@api/agentChatStream.api";
import {
    formatToolDuration,
    formatToolRowSubtitle,
} from "./agentChatToolRow.helpers";
import styles from "./AgentChat.module.scss";

interface Props {
    row: LiveToolRow;
}

// One tool-call row, used both live (spinner → check/error) and inside a
// persisted assistant message. Once complete it shows how long the call took;
// the raw input/result JSON lives behind a per-row expand.
export const AgentChatToolCallRow: React.FC<Props> = ({ row }) => {
    const subtitle = formatToolRowSubtitle(row);
    const duration = row.entry ? formatToolDuration(row.entry.durationMs) : "";
    return (
        <div className={styles.toolCallRow}>
            <div className={styles.toolCallHeader}>
                <StatusIcon status={row.status} />
                <code className={styles.entryName}>{row.name}</code>
                {subtitle.length > 0 && (
                    <span className={styles.toolCallSubtitle}>{subtitle}</span>
                )}
                {duration.length > 0 && (
                    <span className={styles.duration}>{duration}</span>
                )}
            </div>
            {row.entry && <ToolCallDetails entry={row.entry} />}
        </div>
    );
};

const StatusIcon: React.FC<{ status: LiveToolRow["status"] }> = ({
    status,
}) => {
    if (status === "running") {
        return (
            <SpinnerIcon
                className={classNames(
                    styles.toolCallSpinner,
                    "icon-color-gray-500",
                )}
            />
        );
    }
    if (status === "error") {
        return (
            <AlertCircleIcon
                className={classNames(
                    styles.toolCallIconError,
                    "icon-color-red-600",
                )}
            />
        );
    }
    return (
        <CheckIcon
            className={classNames(
                styles.toolCallIconDone,
                "icon-color-green-600",
            )}
        />
    );
};

const ToolCallDetails: React.FC<{ entry: ToolTraceEntry }> = ({ entry }) => {
    if (entry.kind === "web_search") {
        return (
            <details className={styles.toolCallDetails}>
                <summary className={styles.toolCallDetailsSummary}>
                    Details
                </summary>
                <div className={styles.toolEntryBody}>
                    <div className={styles.muted}>
                        searchCallId: {entry.searchCallId}
                    </div>
                    <div className={styles.muted}>status: {entry.status}</div>
                    {entry.durationMs !== null && (
                        <div className={styles.duration}>
                            {entry.durationMs}ms
                        </div>
                    )}
                </div>
            </details>
        );
    }
    return (
        <details className={styles.toolCallDetails}>
            <summary className={styles.toolCallDetailsSummary}>Details</summary>
            <div className={styles.toolEntryBody}>
                <pre className={styles.entryBody}>
                    {JSON.stringify(entry.input, null, 2)}
                </pre>
                <pre
                    className={classNames(styles.entryBody, {
                        [styles.errorBlock]: entry.isError,
                    })}
                >
                    {JSON.stringify(entry.result, null, 2)}
                </pre>
            </div>
        </details>
    );
};
